/**
 * CDN Utilities for managing asset URLs
 * ใช้ค่าเดียวกับ App (window.CDN_BASE_URL / VITE_CDN_URL) — default S3
 */

const DEFAULT_CDN_URL = 'https://huay-cdn-assets-prod.s3.ap-southeast-1.amazonaws.com'
const DEFAULT_THEME_FOLDER = 'HuayAsia'

const isUnsafeLocalCdn = (value: string): boolean => {
  if (!value) return false

  try {
    const parsed = new URL(value)
    const isHttpsPage = typeof window !== 'undefined' && window.location.protocol === 'https:'
    const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'
    return isLocalHost || (isHttpsPage && parsed.protocol !== 'https:')
  } catch {
    return false
  }
}

/**
 * Get CDN base URL (จาก window ที่ inject ตอนโหลด หรือ VITE_CDN_URL หรือ default S3)
 */
export const getCdnBaseUrl = (): string => {
  if (typeof window !== 'undefined' && (window as any).CDN_BASE_URL) {
    const runtimeCdn = (window as any).CDN_BASE_URL as string
    if (!isUnsafeLocalCdn(runtimeCdn)) {
      return runtimeCdn
    }
  }

  const envCdn = import.meta.env.VITE_CDN_URL || DEFAULT_CDN_URL
  if (!isUnsafeLocalCdn(envCdn)) {
    return envCdn
  }

  return DEFAULT_CDN_URL
}

const getRuntimeThemeFolder = (): string => {
  if (typeof window !== 'undefined') {
    const runtimeThemeFolder = String((window as any).__CLASSIC_THEME_FOLDER__ || '').trim()
    if (runtimeThemeFolder) {
      return runtimeThemeFolder
    }
  }

  if (typeof document !== 'undefined') {
    const datasetThemeFolder = document.documentElement.dataset.classicThemeFolder?.trim()
    if (datasetThemeFolder) {
      return datasetThemeFolder
    }
  }

  return DEFAULT_THEME_FOLDER
}

const resolveThemeFolder = (theme?: string): string => {
  const normalizedTheme = String(theme || '').trim()
  return normalizedTheme || getRuntimeThemeFolder()
}

export const getCurrentThemeFolder = (): string => resolveThemeFolder()

// สำหรับ debug (คงไว้)
const CDN_CONFIG = {
  get DEV_BASE_URL() { return getCdnBaseUrl() },
  get PROD_BASE_URL() { return getCdnBaseUrl() },
  IS_DEVELOPMENT: import.meta.env.DEV,
}

/**
 * Generate CDN URL for assets
 * @param path - Asset path relative to CDN root (e.g., 'HuayAsia/img/game-csn/ae.c00d8e6.jpeg')
 * @returns Full CDN URL
 */
export const getCdnUrl = (path: string): string => {
    const baseUrl = getCdnBaseUrl()
    // Remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path
    return `${baseUrl}/${cleanPath}`
}

/**
 * Generate CDN URL for casino game images
 * @param filename - Image filename (e.g., 'ae.c00d8e6.jpeg')
 * @param theme - Theme folder name (default: active runtime theme)
 * @returns Full CDN URL for casino game image
 */
export const getCasinoGameImageUrl = (filename: string, theme?: string): string => {
    return getCdnUrl(`${resolveThemeFolder(theme)}/img/game-csn/${filename}`)
}

/**
 * Generate CDN URL for general images
 * @param filename - Image filename
 * @param theme - Theme folder name (default: active runtime theme)
 * @returns Full CDN URL for image
 */
export const getImageUrl = (filename: string, theme?: string): string => {
    return getCdnUrl(`${resolveThemeFolder(theme)}/img/${filename}`)
}

/**
 * Generate CDN URL for CSS files
 * @param filename - CSS filename
 * @param theme - Theme folder name (default: active runtime theme)
 * @returns Full CDN URL for CSS file
 */
export const getCssUrl = (filename: string, theme?: string): string => {
    return getCdnUrl(`${resolveThemeFolder(theme)}/css/${filename}`)
}

/**
 * Generate CDN URL for JS files
 * @param filename - JS filename
 * @param theme - Theme folder name (default: active runtime theme)
 * @returns Full CDN URL for JS file
 */
export const getJsUrl = (filename: string, theme?: string): string => {
    return getCdnUrl(`${resolveThemeFolder(theme)}/js/${filename}`)
}

// Export CDN configuration for debugging
export const CDN_DEBUG = {
  baseUrl: getCdnBaseUrl(),
  isDevelopment: CDN_CONFIG.IS_DEVELOPMENT,
  devBaseUrl: CDN_CONFIG.DEV_BASE_URL,
  prodBaseUrl: CDN_CONFIG.PROD_BASE_URL,
}
